import { OpenInNewOutlined } from '@mui/icons-material'
import React from 'react'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { PopoverModal } from 'sutra/components/PopoverModal/PopoverModal'

import FilterDisplay from 'components/chart-v2/components/FilterDisplay'
import YogiDivider from 'components/ui/YogiDivider'
import { buildFeedbackUrl } from 'features/ask-yogi/features/conversation/utils/url-builder'
import type { ShopifySummary, TableFilterData } from 'features/ask-yogi/types'

type FilterDisplayRow = {
  field: string
  values?: string[]
  min?: string
  max?: string
}

/**
 * Map a ShopifySummary into the Filter[] shape FilterDisplay renders, so the
 * Shopify branch of the modal shares the Yogi modal's section + chip styling.
 */
const buildShopifyFilters = (summary: ShopifySummary): FilterDisplayRow[] => {
  const filters: FilterDisplayRow[] = []
  if (summary.metrics && summary.metrics.length > 0) {
    filters.push({ field: 'Metrics', values: summary.metrics })
  }
  if (summary.group_by && summary.group_by.length > 0) {
    filters.push({ field: 'Grouped by', values: summary.group_by })
  }
  if (summary.product_filter) {
    filters.push({
      field: 'Products matching',
      values: [summary.product_filter],
    })
  }
  if (summary.date_range_label) {
    const parts = summary.date_range_label.split(' – ')
    if (parts.length === 2) {
      filters.push({ field: 'Date Range', min: parts[0], max: parts[1] })
    } else {
      filters.push({
        field: 'Date Range',
        values: [summary.date_range_label],
      })
    }
  }
  return filters
}

interface TableFilterModalProps {
  open: boolean
  onClose: () => void
  filterData: TableFilterData[]
  projectId: string
}

const TableFilterModal: React.FC<TableFilterModalProps> = ({
  open,
  onClose,
  filterData,
  projectId,
}) => {
  if (!filterData || filterData.length === 0) {
    return null
  }

  const hasShopifySummary = filterData.some((entry) => entry.shopify_summary)
  const hasYogiFilters = filterData.some(
    (entry) => entry.filters && entry.filters.length > 0,
  )
  // Mixed = at least one Shopify-sourced entry AND at least one Yogi-sourced
  // entry. Multi-entry single-source tables fall back to generic "Filter Set N".
  const isMixedSource = hasShopifySummary && hasYogiFilters
  const title = isMixedSource
    ? 'How this table was built from Shopify and Yogi data.'
    : hasShopifySummary
      ? 'How this table was built from your Shopify store.'
      : 'This table was generated using these filters.'

  return (
    <PopoverModal
      title={<div style={{ color: '#28364f' }}>{title}</div>}
      isOpen={open}
      onClose={onClose}
      footer={null}
      width={600}
      content={
        <>
          <YogiDivider
            style={{
              width: 'calc(100% + 40px)',
              marginLeft: -20,
              marginBottom: 16,
            }}
          />

          {filterData.map((entry, index) => {
            const shopifyFilters = entry.shopify_summary
              ? buildShopifyFilters(entry.shopify_summary)
              : null
            const displayFilters = shopifyFilters ?? entry.filters

            return (
              <FilterSection key={entry.result_id}>
                {filterData.length > 1 && (
                  <FilterSetHeader>
                    {isMixedSource
                      ? entry.shopify_summary
                        ? 'Shopify'
                        : 'Yogi'
                      : `Filter Set ${index + 1}`}
                  </FilterSetHeader>
                )}

                {/* Show link to Feedback if filter hash available */}
                {entry.filter_hash && (
                  <LinksContainer>
                    <Button
                      variant="secondary"
                      icon={<OpenInNewOutlined style={{ fontSize: 16 }} />}
                      onClick={() => {
                        const feedbackUrl = buildFeedbackUrl(
                          projectId,
                          entry.filter_hash,
                        )
                        window.open(feedbackUrl, '_blank')
                      }}
                    >
                      View in Feedback
                    </Button>
                  </LinksContainer>
                )}

                {displayFilters && displayFilters.length > 0 ? (
                  <FilterDisplay filters={displayFilters as never} />
                ) : (
                  <EmptyState>No filters applied</EmptyState>
                )}

                {/* Add divider between filter sets if there are multiple */}
                {index < filterData.length - 1 && (
                  <YogiDivider style={{ marginTop: 20, marginBottom: 20 }} />
                )}
              </FilterSection>
            )
          })}
        </>
      }
    />
  )
}

export default TableFilterModal

const FilterSection = styled.div`
  // margin-bottom: 12px;
`

const FilterSetHeader = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: #28364f;
  margin-bottom: 16px;
`

const EmptyState = styled.div`
  font-style: italic;
  color: #666;
  padding: 12px 0;
`

const LinksContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
`
